/** @jsxImportSource @emotion/react */

import {
  EuiPageTemplate,
  EuiFlexGroup,
  EuiFlexItem,
  EuiThemeProvider,
  EuiButton,
} from "@inscopix/ideas-eui";
import { BottomBarButtonProps } from "components/BottomBar/BottomBarButton";
import { useToolEditorTimelineStatus } from "components/ToolEditor/Provider/store/hooks/useToolEditorTimelineStatus";
import { ToolEditorPageKey } from "components/ToolEditor/Provider/store/ToolEditorProvider.store";
import { useToolEditorContext } from "components/ToolEditor/Provider/ToolEditorProvider";
import { useFormikContext } from "formik";
import { useMemo } from "react";

interface ToolEditorBottomBarProps {
  page: ToolEditorPageKey;
}
export const ToolEditorBottomBar = ({ page }: ToolEditorBottomBarProps) => {
  const onClickTimelinePage = useToolEditorContext(
    (s) => s.onClickTimelinePage,
  );
  const hasTestTable = useToolEditorContext((s) => s.testTable !== null);
  const createTestTable = useToolEditorContext((s) => s.createTestTable);
  const isCreatingTestTable = useToolEditorContext(
    (s) => s.isCreatingTestTable,
  );

  const resetDataTableProviderKey = useToolEditorContext(
    (s) => s.resetDataTableProviderKey,
  );

  const isPublishActionEnabled = useToolEditorContext(
    (s) => s.isPublishActionEnabled,
  );

  const timelineStatusByPage = useToolEditorTimelineStatus(page);
  const isContainerImageWarning = useToolEditorContext(
    (s) => s.isContainerImageWarning,
  );

  const isCodePageDisabled = timelineStatusByPage["CODE"].isDisabled;
  const isSpecPageDisabled = timelineStatusByPage["SPEC"].isDisabled;
  const isPublishPageDisabled = timelineStatusByPage["PUBLISH"].isDisabled;
  const isTestPageDisabled = timelineStatusByPage["TEST"].isDisabled;

  const {
    submitForm: handlePublish,
    isSubmitting,
    isValid,
  } = useFormikContext();

  const backProps: BottomBarButtonProps = useMemo(() => {
    const baseProps = { children: "Back", size: "s" };
    switch (page) {
      case "CODE": {
        return { ...baseProps, isDisabled: true };
      }
      case "SPEC": {
        return {
          ...baseProps,
          isDisabled: isCodePageDisabled || isSubmitting || isCreatingTestTable,
          onClick: () => onClickTimelinePage("CODE"),
        };
      }
      case "TEST": {
        return {
          ...baseProps,
          isDisabled: isSpecPageDisabled || isSubmitting || isCreatingTestTable,
          onClick: () => onClickTimelinePage("SPEC"),
        };
      }
      case "PUBLISH": {
        return {
          ...baseProps,
          onClick: () => onClickTimelinePage("TEST"),
          isDisabled: isSubmitting || isCreatingTestTable,
        };
      }
    }
  }, [
    isCodePageDisabled,
    isCreatingTestTable,
    isSpecPageDisabled,
    isSubmitting,
    onClickTimelinePage,
    page,
  ]);

  const nextProps: BottomBarButtonProps = useMemo(() => {
    const baseProps = {
      isLoading: isCreatingTestTable || isSubmitting,
      size: "s",
      fill: true,
    };
    switch (page) {
      case "CODE": {
        return {
          ...baseProps,
          children: "Next",
          isDisabled: isSpecPageDisabled,
          onClick: () => {
            if (isSpecPageDisabled) {
              return;
            }
            onClickTimelinePage("SPEC");
          },
        };
      }
      case "SPEC": {
        return {
          ...baseProps,
          children: "Next",
          isDisabled: isTestPageDisabled,
          onClick: () => {
            if (hasTestTable) {
              onClickTimelinePage("TEST");
            } else {
              void createTestTable().then(() => {
                resetDataTableProviderKey();
                onClickTimelinePage("TEST");
              });
            }
          },
        };
      }
      case "TEST": {
        return {
          ...baseProps,
          children: "Next",
          isDisabled: isPublishPageDisabled,
          onClick: () => {
            if (isPublishPageDisabled) {
              return;
            }
            onClickTimelinePage("PUBLISH");
          },
        };
      }
      case "PUBLISH": {
        return {
          ...baseProps,
          color: "primary",
          children: "Publish",
          isLoading: isSubmitting,
          isDisabled:
            !isValid || isContainerImageWarning || !isPublishActionEnabled,
          onClick: () => void handlePublish(),
        };
      }
    }
  }, [
    createTestTable,
    handlePublish,
    hasTestTable,
    isContainerImageWarning,
    isCreatingTestTable,
    isPublishActionEnabled,
    isPublishPageDisabled,
    isSpecPageDisabled,
    isSubmitting,
    isTestPageDisabled,
    isValid,
    onClickTimelinePage,
    page,
    resetDataTableProviderKey,
  ]);

  return (
    <EuiPageTemplate.BottomBar paddingSize="s" restrictWidth={false}>
      <EuiFlexGroup justifyContent="spaceBetween" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiThemeProvider colorMode="light">
            <EuiButton {...backProps} color="text" />
          </EuiThemeProvider>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiThemeProvider colorMode="light">
            <EuiButton {...nextProps} />
          </EuiThemeProvider>
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiPageTemplate.BottomBar>
  );
};
